import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { BehaviorSubject, defer, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Category } from '../interfaces/category';
import { HttpClient } from '@angular/common/http';
import { Brand } from '../interfaces/brand';
import { IDBProduct, OnlineProduct } from '../interfaces/product';
import { DbProductsList } from '../interfaces/list';
import { Filter, SerializedFilterValues } from '../interfaces/filter';
import { environment } from 'src/environments/environment'; 
import { HomeBlock } from './home-shelf.service';
import { ProductsService } from '../services/products.service';
import { Review } from '../interfaces/review';
import { StorageService } from '../services/storage.service';
// import {
//     getBestsellers,
//     getFeatured,
//     getLatestProducts,
//     getProduct,
//     getRelatedProducts,
//     getSpecialOffers,
//     getTopRated,
//     getShopCategoriesBySlugs,
//     getShopCategoriesTree,
//     getShopCategory,
//     getBrands,
//    // getProductsList,
// } from '../../../fake-server';
//import { getSuggestions } from 'src/fake-server/database/products';

export interface ListOptions {
    page?: number;
    limit?: number;
    sort?: string;
    badges?:string;
    text?:string;
    filterValues?: SerializedFilterValues;
    /// A later page of a scrolling listing: the moment its first page was picked for (DbProductsList.showcaseAt).
    showcaseAt?: number;
}

 
@Injectable({
    providedIn: 'root'
})
export class ShopService 
{
    baseUrl:string = environment.apiUrl; 
    constructor(
        private http: HttpClient, private storage:StorageService,
        private state: TransferState, @Inject(PLATFORM_ID) private platformId: any
    ) 
    { }

    /**
     * Returns category object by slug.
     *
     * @param slug - Unique human-readable category identifier.
     */
    getCategory(slug: string): Observable<Category> 
    {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/categories/power-tools.json
         *
         * where:
         * - power-tools = slug
         */
        // return this.http.get<Category>(`https://example.com/api/shop/categories/${slug}.json`);

        // This is for demonstration purposes only. Remove it and use the code above.
        return; //getShopCategory(slug);
    }

    /**
     * Returns a category tree.
     *
     * @param parent - If a parent is specified then its descendants will be returned.
     * @param depth  - Maximum depth of category tree.
     */
    getCategories(parent: Partial<Category> = null, depth: number = 0): Observable<Category[]> 
    {   
        return this.http.get<Category[]>(this.baseUrl +'/api/Category/GetPopularCategories'); 
    }

    getPopularCategories(): Observable<Category[]> 
    {  
        return this.http.get<Category[]>(this.baseUrl +'/api/Category/GetPopularCategories'); 
    }

    /**
     * Returns an array of the specified categories.
     *
     * @param slugs - Array of slugs.
     * @param depth - Maximum depth of category tree.
     */
    getCategoriesBySlug(slugs: string[], depth: number = 0): Observable<Category[]>
    {
        /**
         * This is what your API endpoint might look like:
         *
         * https://example.com/api/shop/categories.json?slugs=power-tools,measurement&depth=1
         *
         * where:
         * - slugs = slugs.join(',')
         * - depth = depth
         */
        // const params: {[param: string]: string} = {
        //     slugs: slugs.join(','),
        //     depth: depth.toString(),
        // };
        //
        // return this.http.get<Category[]>('https://example.com/api/shop/categories.json', {params});

        // This is for demonstration purposes only. Remove it and use the code above.
        return ;//getShopCategoriesBySlugs(slugs, depth);
    }

    /**
     * Returns paginated products list.
     * If categorySlug is null then a list of all products should be returned.
     *
     * @param categorySlug         - Unique human-readable category identifier.
     * @param options              - Options.
     * @param options.page         - Page number (optional).
     * @param options.limit        - Maximum number of items returned at one time (optional).
     * @param options.sort         - The algorithm by which the list should be sorted (optional).
     * @param options.filterValues - An object whose keys are filter slugs and values ​​are filter values (optional).
     */
    /// shopSlug: a seller's public shop page - that shop's products only (ShopPages on the server). Not shopUid, which is
    /// the seller's own product list in their account.
    getProductsList(categorySlug: string|null, brandSlug: string|null, q:string|null, options: ListOptions, type: string|null = '', shopUid: string|null = '', shopSlug: string|null = null): Observable<DbProductsList>
    {
        return this.getProdCall(categorySlug, brandSlug, q, options, type, shopUid, shopSlug)
    }

    getProdCall(categorySlug: string|null, brandSlug: string|null, query:string|null, options: ListOptions, type: string|null = '', shopUid: string|null = '', shopSlug: string|null = null): Observable<DbProductsList>
    {
        const filtersDef = [
            {type: 'range', slug: 'price', name: 'Price'},
            // {type: 'link', slug: 'brand', name: 'Brand'},
           // {type: 'radio', slug: 'discount', name: 'With Discount'},
          //  {type: 'color', slug: 'color', name: 'Color'},
        ]; 
 
        const page = options.page || 1;
        const limit = options.limit || 20;
        const sort = options.sort || 'default';
        const badge = type || ''; 
        const text = query || '';
        const filterValues = options.filterValues || {}; 
        const filters: Filter[] = this.makeFilters(filtersDef);   
        var model:any = {};
        model.page = page;
        model.limit = limit;
        model.sort = sort;
        model.badges = badge;
        model.filters = filters;
        model.text = text;
        model.filterValues = JSON.stringify(filterValues);
        model.catSlug = categorySlug;
        model.brandSlug = brandSlug;
        if (options.showcaseAt) { model.showcaseAt = options.showcaseAt; }
        if (shopSlug) { model.shopSlug = shopSlug; }

        if (shopUid && shopUid.trim() !== '') 
        {
            return this.http.get<DbProductsList>(this.baseUrl+'/api/UserProduct/GetUserProducts?suid='+shopUid); 
        }
        else
        {
            return this.http.post<DbProductsList>(this.baseUrl+'/api/Product/GetProducts', model); 
        } 
    }

    getShopAvailableSKU(): Observable<string[]> 
    {  
        return this.http.get<string[]>(this.baseUrl +'/api/UserProduct/GetAllAvailableSKUs'); 
    }

    addNewProduct(product:IDBProduct):Observable<IDBProduct>
    {
       return this.http.post<any>(this.baseUrl+'/api/UserProduct/AddNewProduct', product);
    }

    /// Saves an edit to a product that already exists. A separate endpoint from the one
    /// above, not a flag on it: adding mints a new PUID and slug, editing must keep both.
    /// The product is addressed by ID, with PUID sent alongside so the server can find the
    /// row either way. Sending an edit to AddNewProduct is what used to duplicate it.
    updateProduct(product:IDBProduct):Observable<IDBProduct>
    {
       return this.http.post<any>(this.baseUrl+'/api/UserProduct/UpdateProduct', product);
    }

    uploadProductImage(files:FormData):Observable<string[]>
    {
       return this.http.post<any>(this.baseUrl+'/api/Image/UploadProductImage', files);
    }

    uploadProductVideo(files:FormData):Observable<string[]>
    {
       return this.http.post<any>(this.baseUrl+'/api/Image/UploadProductVideo', files);
    }

    getProduct(productSlug: string): Observable<IDBProduct> 
    { 
        const params: {[param: string]: string} = {}; 
        if (productSlug) 
        {
            params.slug = productSlug;
        }

        return this.http.get<IDBProduct>(this.baseUrl+`/api/Product/GetProductBySlug`, {params}); 
    }

    getpvcount(slug:string): Observable<Brand[]> 
    { 
        return this.http.get<Brand[]>(this.baseUrl+'/api/Product/pvcount?slug='+slug);  
    }


    /**
     * Returns popular brands.
     */
    getPopularBrands(): Observable<Brand[]> 
    { 
        return this.http.get<Brand[]>(this.baseUrl+'/api/Category/GetAllBrands');  
    }

    getBrandsForPage(type:string, limit:number): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetBrandsForPage?type=${type}&limit=${limit}`); 
    } 
    
    getTopRated(categorySlug: string, limit: number = 12, brandSlug: string = ''): Observable<OnlineProduct[]> 
    {
        return this.http.get<OnlineProduct[]>(this.baseUrl+`/api/Product/GetTopRatedProducts?cat=${categorySlug}&brand=${brandSlug}&limit=${limit}`); 
    } 

    getFeaturedProducts(categorySlug: string, limit: number = 12, brandSlug: string = ''): Observable<OnlineProduct[]> 
    { 
        return this.http.get<OnlineProduct[]>(this.baseUrl+`/api/Product/GetFeaturedProducts?cat=${categorySlug}&brand=${brandSlug}&limit=${limit}`); 
    }

    /// One shelf of the home page or the blog's pages (HomeShelves on the server): 'latest', 'super-saver', 'best-deals',
    /// 'spotlight', 'luxury-deals', 'mega-offers' - a random pick of the owner's bucket, topped up by the shelf's own
    /// rule. The server render's pick is carried and KEPT: asked again, the browser would get another pick and the
    /// cards would change under the visitor. A tab opened later asks, and gets a pick of its own.
    getHomeShelf(shelf: string, limit: number): Observable<OnlineProduct[]>
    {
        return this.carried('shelf:' + shelf + ':' + limit, [] as OnlineProduct[],
            () => this.http.get<OnlineProduct[]>(this.baseUrl + '/api/Product/GetHomeShelf', { params: { shelf, limit: String(limit) } }));
    }

    /// The home page's blocks: their titles and tab names, the owner's or the defaults. Carried and kept like the shelves.
    getHomeBlocks(): Observable<HomeBlock[]>
    {
        return this.carried('home-blocks', null as HomeBlock[],
            () => this.http.get<HomeBlock[]>(this.baseUrl + '/api/Product/GetHomeBlocks'));
    }

    /// An answer for a part of the page that must be in the server render (a search engine reads the cards) and must not
    /// jump when the browser takes the page over: on the server the answer is carried in the page, and the browser shows
    /// that and does not ask again. The server asked with the visitor's country (AuthInterceptor); for the signed-in
    /// owner it asked as no country, which is how the owner's browser sees the lists too. A failed request is the
    /// fallback, never an error.
    private carried<T>(name: string, fallback: T, ask: () => Observable<T>): Observable<T>
    {
        const key = makeStateKey<T>(name);

        return defer(() => {
            const carried = this.state.get<T>(key, null);
            const fresh$ = ask().pipe(
                tap(answer => {
                    if (isPlatformServer(this.platformId)) {
                        this.state.set(key, answer || fallback);
                    }
                }),
                catchError(() => of(carried || fallback)));

            if (!carried || (Array.isArray(carried) && carried.length === 0)) {
                return fresh$;
            }

            // Only the first draw of the page takes what the server carried; a later visit asks afresh.
            this.state.remove(key);
            return of(carried);
        });
    }

    getLatestProducts(categorySlug: string = '', limit: number = null, brandSlug: string = ''): Observable<OnlineProduct[]> 
    { 
        return this.http.get<OnlineProduct[]>(this.baseUrl+`/api/Product/GetLatestProducts?cat=${categorySlug}&brand=${brandSlug}&limit=${limit}`); 
    }

    getRelatedProducts(product: Partial<OnlineProduct>): Observable<OnlineProduct[]> 
    {
        const params: {[param: string]: any} = {}; 
        params.categorySlug = product.CategorySlug;
        params.productName = product.Name;
        params.puid = product.PUID;
        params.categoryTree = product.CategoryTree; 
        params.brandSlug = product.BrandSlug;  
        params.price = product.Price;

        return this.http.get<OnlineProduct[]>(this.baseUrl+'/api/Product/GetRealatedProducts', {params});
    }

    /// getRelatedProducts, carried from the server render in the page. The picks are random, so a second answer in the
    /// browser would swap every card - and a sold-out product's "in stock now" cards beside its price - as the page woke
    /// up. The browser shows what the server chose and does not ask again; only a page the server did not draw asks.
    /// A failed request is an empty list, never an error.
    getRelatedProductsCarried(product: Partial<OnlineProduct>): Observable<OnlineProduct[]>
    {
        const key = makeStateKey<OnlineProduct[]>('related:' + (product && product.PUID));

        return defer(() => {
            const carried = this.state.get<OnlineProduct[]>(key, null);
            if (carried && !isPlatformServer(this.platformId)) {
                this.state.remove(key);
                return of(carried);
            }
            return this.getRelatedProducts(product).pipe(
                tap(list => {
                    if (isPlatformServer(this.platformId)) {
                        this.state.set(key, list || []);
                    }
                }),
                catchError(() => of([] as OnlineProduct[])));
        });
    }

    getSuggestions(query: string, categorySlug: string = null): Observable<OnlineProduct[]> {
       
        const params: {[param: string]: string} = {query}; 
        if (categorySlug) {
            params.category = categorySlug;
        } 

        return this.http.get<OnlineProduct[]>(this.baseUrl+'/api/Product/GetSearchSuggestions', {params}); 
    }

    //#region Review
    /// { Success, Message, Reason }: only a buyer of the product may review it, once.
    postReview(model:any): Observable<any> { 
        return this.http.post<any>(this.baseUrl+'/api/Review/PostNewReview', model); 
    }
    /// { CanReview, Reason, Message, MinLength, MaxLength } for the signed-in visitor.
    canReview(productId: number): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/Review/CanReview/' + productId);
    }
    getReviews(puid:any, page = 1): Observable<any> { 
        return this.http.get<any>(this.baseUrl+'/api/Review/GetProductReviews/'+puid+'?page='+page); 
    }
    //#endregion

    makeFilters(filtersDef, min = 0, max = 500000) 
    {
        const result = []; 
        filtersDef.forEach(filterDef => 
        {
            if (filterDef.type === 'range') 
            { 
                /** Calculates the number of digits for rounding. */
                let digit = Math.max(Math.ceil(max).toString().length - 2, 1);
    
                digit = Math.pow(10, digit);
                max = Math.ceil(max / digit) * digit;
                min = Math.floor(min / digit) * digit;
    
                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: [min, max],
                    // options
                    min,
                    max,
                });
            } 
            else if (filterDef.type === 'check' || filterDef.type === 'radio' || filterDef.type === 'color' || filterDef.type === 'link') 
            {  
                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: filterDef.type === 'radio' ? [] : [],
                    items:[],
                });
            }
        });
    
        return result;
    } 


    /// Remembers that a product was viewed, as a SLUG.
    ///
    /// It used to store the whole product object. That copy was written once and never updated,
    /// so an edited title, a new price or a replaced image never reached this list - the same
    /// product would sit there under its old crawled name at its old price, sometimes next to
    /// its own updated self. It de-duplicated on `ID`, which made that worse: the product page
    /// works in Products.ID and the listing model carries OnlineProducts.ID, so two views of
    /// one product compared unequal and both were kept.
    ///
    /// The slug is the one identifier that is stable across both models and is also the key the
    /// storefront looks products up by.
    setRecentView(product: any): void
    {
        if (!product || !product.Slug) { return; }

        let slugs: string[] = this.readViewSlugs();

        slugs = slugs.filter(s => s !== product.Slug);
        slugs.unshift(product.Slug);

        // Newest first, so trimming drops the oldest.
        if (slugs.length > 15) { slugs = slugs.slice(0, 15); }

        this.storage.setItem('views', JSON.stringify(slugs));
    }

    /// Reads the stored slugs, converting anything left over from when whole products were
    /// stored. Without this, an existing visitor's list would throw on the first read.
    private readViewSlugs(): string[]
    {
        try
        {
            const raw = JSON.parse(this.storage.getItem('views') || '[]');
            if (!Array.isArray(raw)) { return []; }

            return raw
                .map(entry => typeof entry === 'string' ? entry : (entry && entry.Slug))
                .filter(slug => !!slug);
        }
        catch (e)
        {
            return [];
        }
    }

    /// Current data for the remembered products. One request, and a product that has since been
    /// unpublished or deleted simply drops out of the list. fillTo: fewer views than that are topped
    /// up, after them, with products related to them (the server picks).
    getRecentViews(fillTo: number = 0):Observable<OnlineProduct[]> {
        const slugs = this.readViewSlugs();

        if (slugs.length === 0) { return of([]); }

        return this.http.post<OnlineProduct[]>(this.baseUrl + '/api/Product/GetProductsBySlugs', { slugs, fillTo });
    }

    //REGION DELETE
    /// confirm: for a product that has been sold, its SKU typed by the owner (the server checks it).
    deleteProd(puid:any, sid:number, confirm:string = null): Observable<any> {
        return this.http.delete<any>(this.baseUrl+'/api/UserProduct/DeleteProduct/'+puid+'/'+sid + (confirm ? '?confirm=' + encodeURIComponent(confirm) : ''));
    }
}
